import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { modulesAPI, milestonesAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ArrowLeft, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_COLORS = {
  not_started: 'bg-red-100 text-red-700',
  in_progress: 'bg-yellow-100 text-yellow-700',
  completed: 'bg-green-100 text-green-700',
  testing: 'bg-blue-100 text-blue-700',
  blocked: 'bg-gray-800 text-white',
};

export default function ModuleDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [module, setModule] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [moduleRes, tasksRes] = await Promise.all([
        modulesAPI.getOne(id),
        milestonesAPI.getAll({ module_id: id }),
      ]);
      setModule(moduleRes.data);
      setTasks(tasksRes.data);
    } catch (err) {
      toast.error('Failed to load module');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <DashboardLayout pageTitle="Module Details">
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-[#CFFF40] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (!module) {
    return (
      <DashboardLayout pageTitle="Module Not Found">
        <div className="text-center py-12">
          <p className="text-[#666666]">Module not found</p>
          <Button onClick={() => navigate('/modules')} className="mt-4">
            Back to Modules
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout pageTitle={module.name}>
      <div className="space-y-6" data-testid="module-detail-page">
        {/* Back Button */}
        <Button variant="ghost" onClick={() => navigate('/modules')} className="mb-4">
          <ArrowLeft size={16} className="mr-2" />
          Back to Modules
        </Button>

        {/* Header Card */}
        <div className="bg-white border border-[#E0E0E0] rounded-lg p-6">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-2xl font-bold text-[#111111]">{module.name}</h1>
              <p className="text-[#666666] mt-1">{module.short_description}</p>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-[#111111]">
                {module.price > 0 ? `$${module.price}` : 'Included'}
                <span className="text-sm font-normal text-[#666666]">/mo</span>
              </p>
              <p className="text-sm text-[#666666]">{module.restaurant_count} restaurants</p>
            </div>
          </div>
          
          <div className="mt-4">
            <div className="flex items-center justify-between text-sm mb-1">
              <span className="text-[#666666]">Development Progress</span>
              <span className="font-medium">{module.progress}%</span>
            </div>
            <Progress value={module.progress} className="h-2" />
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="bg-white border border-[#E0E0E0]">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="pricing">Pricing</TabsTrigger>
            <TabsTrigger value="technical">Technical</TabsTrigger>
            <TabsTrigger value="tasks">Milestones</TabsTrigger>
            <TabsTrigger value="restaurants">Restaurants</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-4">
            <div className="bg-white border border-[#E0E0E0] rounded-lg p-6 space-y-4">
              <div>
                <h3 className="font-semibold text-[#111111] mb-2">Full Description</h3>
                <p className="text-[#666666]">{module.full_description || 'No description available'}</p>
              </div>
              
              {module.key_features?.length > 0 && (
                <div>
                  <h3 className="font-semibold text-[#111111] mb-2">Key Features</h3>
                  <ul className="list-disc list-inside text-[#666666] space-y-1">
                    {module.key_features.map((f, i) => (
                      <li key={`feature-${i}-${f}`}>{f}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              {module.target_audience && (
                <div>
                  <h3 className="font-semibold text-[#111111] mb-2">Target Audience</h3>
                  <p className="text-[#666666]">{module.target_audience}</p>
                </div>
              )}
              
              {module.value_proposition && (
                <div>
                  <h3 className="font-semibold text-[#111111] mb-2">Value Proposition</h3>
                  <p className="text-[#666666]">{module.value_proposition}</p>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="pricing" className="mt-4">
            <div className="bg-white border border-[#E0E0E0] rounded-lg p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h3 className="font-semibold text-[#111111] mb-2">Monthly Price</h3>
                  <p className="text-2xl font-bold text-[#111111]">${module.price}</p>
                </div>
                <div>
                  <h3 className="font-semibold text-[#111111] mb-2">Pricing Model</h3>
                  <p className="text-[#666666] capitalize">{module.pricing_model || 'Monthly'}</p>
                </div>
              </div>
              
              {module.discounts && (
                <div>
                  <h3 className="font-semibold text-[#111111] mb-2">Discounts</h3>
                  <p className="text-[#666666]">{module.discounts}</p>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="technical" className="mt-4">
            <div className="bg-white border border-[#E0E0E0] rounded-lg p-6 space-y-4">
              {module.requirements && (
                <div>
                  <h3 className="font-semibold text-[#111111] mb-2">Requirements</h3>
                  <p className="text-[#666666] whitespace-pre-wrap">{module.requirements}</p>
                </div>
              )}
              
              {module.api_endpoints && (
                <div>
                  <h3 className="font-semibold text-[#111111] mb-2">API Endpoints</h3>
                  <pre className="bg-[#F5F5F5] p-3 rounded text-sm overflow-x-auto">{module.api_endpoints}</pre>
                </div>
              )}
              
              {module.database_schema && (
                <div>
                  <h3 className="font-semibold text-[#111111] mb-2">Database Schema</h3>
                  <pre className="bg-[#F5F5F5] p-3 rounded text-sm overflow-x-auto">{module.database_schema}</pre>
                </div>
              )}
              
              {module.integrations && (
                <div>
                  <h3 className="font-semibold text-[#111111] mb-2">Integrations</h3>
                  <p className="text-[#666666]">{module.integrations}</p>
                </div>
              )}
              
              {module.github_link && (
                <a
                  href={module.github_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-[#FF6B35] hover:underline"
                >
                  <ExternalLink size={16} />
                  View on GitHub
                </a>
              )}
            </div>
          </TabsContent>

          <TabsContent value="tasks" className="mt-4">
            <div className="bg-white border border-[#E0E0E0] rounded-lg overflow-hidden">
              {tasks.length > 0 ? (
                <table className="w-full">
                  <thead className="bg-[#F5F5F5]">
                    <tr>
                      <th className="text-left p-3 text-sm font-medium text-[#666666]">Milestone</th>
                      <th className="text-left p-3 text-sm font-medium text-[#666666]">Status</th>
                      <th className="text-left p-3 text-sm font-medium text-[#666666]">Assignee</th>
                      <th className="text-left p-3 text-sm font-medium text-[#666666]">Due Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tasks.map(task => (
                      <tr key={task.id} className="border-t border-[#E0E0E0] hover:bg-[#F5F5F5]">
                        <td className="p-3 text-sm">{task.title}</td>
                        <td className="p-3">
                          <Badge className={STATUS_COLORS[task.status] || 'bg-gray-100'}>
                            {task.status?.replace('_', ' ')}
                          </Badge>
                        </td>
                        <td className="p-3 text-sm text-[#666666]">{task.assignee_name || '-'}</td>
                        <td className="p-3 text-sm text-[#666666]">{task.end_date || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="p-6 text-center text-[#666666]">No milestones found</div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="restaurants" className="mt-4">
            <div className="bg-white border border-[#E0E0E0] rounded-lg overflow-hidden">
              {module.restaurants?.length > 0 ? (
                <table className="w-full">
                  <thead className="bg-[#F5F5F5]">
                    <tr>
                      <th className="text-left p-3 text-sm font-medium text-[#666666]">Restaurant</th>
                      <th className="text-left p-3 text-sm font-medium text-[#666666]">Status</th>
                      <th className="text-left p-3 text-sm font-medium text-[#666666]">Activation</th>
                      <th className="text-right p-3 text-sm font-medium text-[#666666]">Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {module.restaurants.map(r => (
                      <tr 
                        key={r.id} 
                        className="border-t border-[#E0E0E0] hover:bg-[#F5F5F5] cursor-pointer"
                        onClick={() => navigate(`/restaurants/${r.id}`)}
                      >
                        <td className="p-3 text-sm font-medium">{r.name}</td>
                        <td className="p-3">
                          <Badge className={r.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}>
                            {r.status}
                          </Badge>
                        </td>
                        <td className="p-3 text-sm text-[#666666]">{r.activation_date || '-'}</td>
                        <td className="p-3 text-sm text-right font-medium">${r.monthly_price}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-[#F5F5F5]">
                    <tr>
                      <td colSpan={3} className="p-3 text-sm font-medium">Total Restaurants</td>
                      <td className="p-3 text-right font-bold">{module.restaurants?.length || 0}</td>
                    </tr>
                  </tfoot>
                </table>
              ) : (
                <div className="p-6 text-center text-[#666666]">No restaurants using this module</div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
